import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { inventarioService, categoriasService, familiasService } from '../../services/tiendaService';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const ORANGE = '#FB923C';
const RED = '#F87171';
const YELLOW = '#FBBF24';

const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtPct = (n) => typeof n === 'number' ? `${fmt(n * 100, 1)}%` : '—';

const TABS = [
  { id: 'valoracion', label: 'Valoración', icon: 'DollarSign' },
  { id: 'margen', label: 'Rentabilidad', icon: 'Percent' },
  { id: 'stock', label: 'Stock', icon: 'Package' },
  { id: 'alertas', label: 'Alertas', icon: 'AlertTriangle' },
];

const RankingTable = ({ title, items, columns, color = ACCENT }) => (
  <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
    <div className="px-4 py-3" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <h3 className="font-oranienbaum font-bold text-white text-sm">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <th className="px-3 py-2 text-left"><span className="font-oranienbaum text-white/30 text-[10px] uppercase">#</span></th>
            {columns?.map(col => (
              <th key={col?.key} className="px-3 py-2 text-left whitespace-nowrap">
                <span className="font-oranienbaum text-white/30 text-[10px] uppercase">{col?.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items?.slice(0, 10)?.map((item, i) => (
            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
              <td className="px-3 py-2">
                <span className="font-oranienbaum text-xs font-bold" style={{ color: i < 3 ? color : 'rgba(255,255,255,0.30)' }}>#{i + 1}</span>
              </td>
              {columns?.map(col => (
                <td key={col?.key} className="px-3 py-2">
                  <span className="font-oranienbaum text-xs" style={{ color: col?.highlight ? color : 'rgba(255,255,255,0.70)' }}>
                    {col?.render ? col?.render(item) : (item?.[col?.key] ?? '—')}
                  </span>
                </td>
              ))}
            </tr>
          ))}
          {items?.length === 0 && (
            <tr><td colSpan={columns?.length + 1} className="px-4 py-8 text-center">
              <p className="font-oranienbaum text-white/30 text-xs">Sin datos disponibles</p>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const TiendaAnalitica = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('valoracion');
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [inv, cats, fams] = await Promise.all([
        inventarioService?.getAll({}),
        categoriasService?.getAll(),
        familiasService?.getAll(),
      ]);
      setItems(inv);
      setCategorias(cats);
      setFamilias(fams);
    } catch (e) {
      setError(e?.message || 'Error al cargar analítica');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Cálculos de rankings ──────────────────────────────────────────────────
  const enrichedItems = items?.map(item => {
    const iva = item?.producto?.tipo_iva ?? 10;
    const costeSinIva = item?.coste_medio ?? 0;
    const costeConIva = costeSinIva * (1 + iva / 100);
    const pvp = item?.producto?.pvp_venta ?? item?.producto?.pvp_sin_iva ?? null;
    const stock = item?.stock_actual ?? 0;
    const valorCoste = stock * costeSinIva;
    const valorCosteConIva = stock * costeConIva;
    const valorPvp = pvp != null ? stock * pvp : null;
    const margenPot = valorPvp != null ? valorPvp - valorCosteConIva : null;
    const cat = categorias?.find(c => c?.id === item?.producto?.categoria?.id);
    const familia = familias?.find(f => f?.id === cat?.familia_id);
    return { ...item, costeSinIva, costeConIva, pvp, stock, valorCoste, valorCosteConIva, valorPvp, margenPot, cat, familia };
  });

  // Rankings valoración
  const rankingValorCoste = [...enrichedItems]?.sort((a, b) => (b?.valorCoste ?? 0) - (a?.valorCoste ?? 0));
  const rankingValorPvp = [...enrichedItems]?.filter(i => i?.valorPvp != null)?.sort((a, b) => (b?.valorPvp ?? 0) - (a?.valorPvp ?? 0));
  const rankingMenorValor = [...enrichedItems]?.filter(i => i?.stock > 0)?.sort((a, b) => (a?.valorCoste ?? 0) - (b?.valorCoste ?? 0));

  // Rankings por categoría
  const catValores = categorias?.map(cat => {
    const catItems = enrichedItems?.filter(i => i?.cat?.id === cat?.id);
    const totalCoste = catItems?.reduce((s, i) => s + (i?.valorCoste ?? 0), 0);
    const totalPvp = catItems?.reduce((s, i) => s + (i?.valorPvp ?? 0), 0);
    return { ...cat, totalCoste, totalPvp, numProductos: catItems?.length };
  })?.filter(c => c?.totalCoste > 0)?.sort((a, b) => b?.totalCoste - a?.totalCoste);

  // Rankings por familia
  const famValores = familias?.map(fam => {
    const famItems = enrichedItems?.filter(i => i?.familia?.id === fam?.id);
    const totalCoste = famItems?.reduce((s, i) => s + (i?.valorCoste ?? 0), 0);
    const totalPvp = famItems?.reduce((s, i) => s + (i?.valorPvp ?? 0), 0);
    return { ...fam, totalCoste, totalPvp, numProductos: famItems?.length };
  })?.filter(f => f?.totalCoste > 0)?.sort((a, b) => b?.totalCoste - a?.totalCoste);

  // Rankings margen
  const rankingMayorMargen = [...enrichedItems]?.filter(i => i?.producto?.margen_real != null)?.sort((a, b) => (b?.producto?.margen_real ?? 0) - (a?.producto?.margen_real ?? 0));
  const rankingMenorMargen = [...enrichedItems]?.filter(i => i?.producto?.margen_real != null)?.sort((a, b) => (a?.producto?.margen_real ?? 0) - (b?.producto?.margen_real ?? 0));
  const rankingBajoObjetivo = enrichedItems?.filter(i => {
    const margenReal = i?.producto?.margen_real;
    const margenObj = i?.producto?.margen_objetivo ?? i?.cat?.margen_objetivo;
    return margenReal != null && margenObj != null && margenReal < margenObj;
  })?.sort((a, b) => {
    const diffA = (a?.producto?.margen_objetivo ?? a?.cat?.margen_objetivo ?? 0) - (a?.producto?.margen_real ?? 0);
    const diffB = (b?.producto?.margen_objetivo ?? b?.cat?.margen_objetivo ?? 0) - (b?.producto?.margen_real ?? 0);
    return diffB - diffA;
  });

  // Rankings stock
  const rankingMayorStock = [...enrichedItems]?.sort((a, b) => (b?.stock ?? 0) - (a?.stock ?? 0));
  const rankingSinStock = enrichedItems?.filter(i => (i?.stock ?? 0) <= 0);
  const rankingBajoMinimo = enrichedItems?.filter(i => {
    const stock = i?.stock ?? 0;
    const min = i?.producto?.stock_minimo ?? 0;
    return stock > 0 && stock <= min;
  });

  // KPIs totales
  const totalCoste = enrichedItems?.reduce((s, i) => s + (i?.valorCoste ?? 0), 0);
  const totalCosteConIva = enrichedItems?.reduce((s, i) => s + (i?.valorCosteConIva ?? 0), 0);
  const totalPvp = enrichedItems?.reduce((s, i) => s + (i?.valorPvp ?? 0), 0);
  const margenTotal = totalPvp - totalCosteConIva;

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${PURPLE}15`, border: `1px solid ${PURPLE}30` }}>
              <Icon name="BarChart2" size={18} style={{ color: PURPLE }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Analítica de Inventario</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">Rankings y análisis de productos · tienda</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/tienda-inventario')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum transition-all"
            style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, color: ACCENT }}>
            <Icon name="Package" size={15} />Inventario
          </button>
          <button onClick={loadData} className="p-2.5 rounded-xl transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={14} />
          </button>
        </div>
      </div>

      {/* KPIs resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Valor Coste s/IVA', value: fmtEur(totalCoste), icon: 'DollarSign', color: BLUE },
          { label: 'Valor Coste c/IVA', value: fmtEur(totalCosteConIva), icon: 'Receipt', color: PURPLE },
          { label: 'Valor Potencial PVP', value: fmtEur(totalPvp), icon: 'TrendingUp', color: GOLD },
          { label: 'Margen Potencial', value: fmtEur(margenTotal), icon: 'Percent', color: ACCENT },
        ]?.map((k, i) => (
          <div key={i} className="rounded-xl p-4 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${k?.color}15`, border: `1px solid ${k?.color}25` }}>
              <Icon name={k?.icon} size={16} style={{ color: k?.color }} />
            </div>
            <div>
              <p className="font-oranienbaum font-bold text-white text-lg">{loading ? '...' : k?.value}</p>
              <p className="font-oranienbaum text-white/45 text-xs">{k?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl mb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', width: 'fit-content' }}>
        {TABS?.map(tab => (
          <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-oranienbaum transition-all"
            style={activeTab === tab?.id
              ? { background: `${PURPLE}20`, color: PURPLE, border: `1px solid ${PURPLE}40` }
              : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
            <Icon name={tab?.icon} size={14} />
            {tab?.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
          <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
          <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#A78BFA] animate-spin" />
          <p className="font-oranienbaum text-white/40 text-sm">Calculando rankings...</p>
        </div>
      ) : (
        <>
          {/* TAB: VALORACIÓN */}
          {activeTab === 'valoracion' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RankingTable
                  title="Mayor valor de stock a coste"
                  items={rankingValorCoste}
                  color={BLUE}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                    { key: 'stock', label: 'Stock', render: i => fmt(i?.stock, 0) },
                    { key: 'valorCoste', label: 'Valor Coste', render: i => fmtEur(i?.valorCoste), highlight: true },
                  ]}
                />
                <RankingTable
                  title="Mayor valor potencial a PVP"
                  items={rankingValorPvp}
                  color={GOLD}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                    { key: 'pvp', label: 'PVP', render: i => fmtEur(i?.pvp) },
                    { key: 'valorPvp', label: 'Valor PVP', render: i => fmtEur(i?.valorPvp), highlight: true },
                  ]}
                />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RankingTable
                  title="Categorías con mayor inversión"
                  items={catValores}
                  color={PURPLE}
                  columns={[
                    { key: 'nombre', label: 'Categoría' },
                    { key: 'numProductos', label: 'Productos' },
                    { key: 'totalCoste', label: 'Valor Coste', render: i => fmtEur(i?.totalCoste), highlight: true },
                  ]}
                />
                <RankingTable
                  title="Familias con mayor valor de stock"
                  items={famValores}
                  color={ACCENT}
                  columns={[
                    { key: 'nombre', label: 'Familia' },
                    { key: 'numProductos', label: 'Productos' },
                    { key: 'totalCoste', label: 'Valor Coste', render: i => fmtEur(i?.totalCoste), highlight: true },
                  ]}
                />
              </div>
            </div>
          )}

          {/* TAB: RENTABILIDAD */}
          {activeTab === 'margen' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RankingTable
                  title="Productos con mayor margen real"
                  items={rankingMayorMargen}
                  color={ACCENT}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                    { key: 'margenReal', label: 'Margen Real', render: i => fmtPct(i?.producto?.margen_real), highlight: true },
                    { key: 'pvp', label: 'PVP', render: i => fmtEur(i?.pvp) },
                  ]}
                />
                <RankingTable
                  title="Productos con menor margen real"
                  items={rankingMenorMargen}
                  color={RED}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                    { key: 'margenReal', label: 'Margen Real', render: i => fmtPct(i?.producto?.margen_real), highlight: true },
                    { key: 'pvp', label: 'PVP', render: i => fmtEur(i?.pvp) },
                  ]}
                />
              </div>
              <RankingTable
                title="Productos por debajo del margen objetivo"
                items={rankingBajoObjetivo}
                color={ORANGE}
                columns={[
                  { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                  { key: 'margenObj', label: 'Margen Obj.', render: i => fmtPct(i?.producto?.margen_objetivo ?? i?.cat?.margen_objetivo) },
                  { key: 'margenReal', label: 'Margen Real', render: i => fmtPct(i?.producto?.margen_real), highlight: true },
                  { key: 'diferencia', label: 'Diferencia', render: i => {
                    const obj = i?.producto?.margen_objetivo ?? i?.cat?.margen_objetivo ?? 0;
                    const real = i?.producto?.margen_real ?? 0;
                    return <span style={{ color: RED }}>{fmtPct(real - obj)}</span>;
                  }},
                ]}
              />
            </div>
          )}

          {/* TAB: STOCK */}
          {activeTab === 'stock' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RankingTable
                  title="Productos con mayor stock"
                  items={rankingMayorStock}
                  color={ACCENT}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                    { key: 'stock', label: 'Stock', render: i => fmt(i?.stock, 0), highlight: true },
                    { key: 'valorCoste', label: 'Valor Coste', render: i => fmtEur(i?.valorCoste) },
                  ]}
                />
                <RankingTable
                  title="Productos bajo mínimo"
                  items={rankingBajoMinimo}
                  color={YELLOW}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                    { key: 'stock', label: 'Stock Actual', render: i => fmt(i?.stock, 0), highlight: true },
                    { key: 'minimo', label: 'Stock Mín.', render: i => fmt(i?.producto?.stock_minimo, 0) },
                  ]}
                />
              </div>
              {rankingSinStock?.length > 0 && (
                <RankingTable
                  title={`Productos sin stock (${rankingSinStock?.length})`}
                  items={rankingSinStock}
                  color={RED}
                  columns={[
                    { key: 'nombre', label: 'Producto', render: i => i?.producto?.nombre || '—' },
                    { key: 'categoria', label: 'Categoría', render: i => i?.cat?.nombre || '—' },
                    { key: 'pvp', label: 'PVP', render: i => fmtEur(i?.pvp) },
                  ]}
                />
              )}
            </div>
          )}

          {/* TAB: ALERTAS */}
          {activeTab === 'alertas' && (
            <div className="space-y-4">
              {rankingBajoMinimo?.length > 0 && (
                <div className="p-4 rounded-xl" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.20)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="AlertTriangle" size={16} style={{ color: YELLOW }} />
                    <h3 className="font-oranienbaum font-bold text-white text-sm">{rankingBajoMinimo?.length} productos bajo mínimo</h3>
                  </div>
                  <div className="space-y-2">
                    {rankingBajoMinimo?.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <span className="font-oranienbaum text-white/80 text-xs">{item?.producto?.nombre}</span>
                        <span className="font-oranienbaum text-xs" style={{ color: YELLOW }}>Stock: {fmt(item?.stock, 0)} / Mín: {fmt(item?.producto?.stock_minimo, 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rankingSinStock?.length > 0 && (
                <div className="p-4 rounded-xl" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="XCircle" size={16} style={{ color: RED }} />
                    <h3 className="font-oranienbaum font-bold text-white text-sm">{rankingSinStock?.length} productos sin stock</h3>
                  </div>
                  <div className="space-y-2">
                    {rankingSinStock?.slice(0, 10)?.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <span className="font-oranienbaum text-white/80 text-xs">{item?.producto?.nombre}</span>
                        <span className="font-oranienbaum text-xs" style={{ color: RED }}>Agotado</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rankingBajoObjetivo?.length > 0 && (
                <div className="p-4 rounded-xl" style={{ background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.20)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="TrendingDown" size={16} style={{ color: ORANGE }} />
                    <h3 className="font-oranienbaum font-bold text-white text-sm">{rankingBajoObjetivo?.length} productos con margen por debajo del objetivo</h3>
                  </div>
                  <div className="space-y-2">
                    {rankingBajoObjetivo?.slice(0, 10)?.map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <span className="font-oranienbaum text-white/80 text-xs">{item?.producto?.nombre}</span>
                        <span className="font-oranienbaum text-xs" style={{ color: ORANGE }}>
                          Real: {fmtPct(item?.producto?.margen_real)} / Obj: {fmtPct(item?.producto?.margen_objetivo ?? item?.cat?.margen_objetivo)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rankingBajoMinimo?.length === 0 && rankingSinStock?.length === 0 && rankingBajoObjetivo?.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-16">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}15` }}>
                    <Icon name="CheckCircle" size={24} style={{ color: `${ACCENT}60` }} />
                  </div>
                  <p className="font-oranienbaum text-white/40 text-sm">No hay alertas activas</p>
                </div>
              )}
            </div>
          )}

          {/* Nota sobre ventas */}
          <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2">
              <Icon name="Info" size={14} className="text-white/30 flex-shrink-0" />
              <p className="font-oranienbaum text-white/30 text-xs">
                Los rankings de ventas (más vendidos, rotación, facturación) estarán disponibles cuando existan datos de ventas registradas.
                Aún no hay suficientes ventas registradas.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TiendaAnalitica;
